import React, { useState, useEffect } from 'react';
import {
    BarChart3,
    ArrowLeft,
    CheckCircle,
    XCircle,
    Target,
    Loader2,
    ChevronRight
} from 'lucide-react';
import { getAuth } from 'firebase/auth';

const API_URL = process.env.REACT_APP_API_URL;

const StatCard = ({ title, stats, color, category, onReviewClick }) => {
    const colorClasses = {
        purple: {
            bg: 'bg-purple-50',
            border: 'border-purple-200',
            title: 'text-purple-800',
            accent: 'text-purple-600'
        },
        blue: {
            bg: 'bg-blue-50',
            border: 'border-blue-200',
            title: 'text-blue-800',
            accent: 'text-blue-600'
        }
    };

    const colors = colorClasses[color] || colorClasses.purple;

    return (
        <div className={`${colors.bg} ${colors.border} border-2 rounded-2xl p-6`}>
            <h3 className={`text-xl font-bold ${colors.title} mb-4`}>{title}</h3>

            <div className="grid grid-cols-2 gap-4 mb-4">
                {/* Correct - Clickable */}
                <button
                    onClick={() => stats.correct > 0 && onReviewClick(category, 'correct')}
                    disabled={stats.correct === 0}
                    className={`bg-white rounded-xl p-4 text-center transition-all ${
                        stats.correct > 0
                            ? 'hover:shadow-md hover:scale-[1.02] cursor-pointer'
                            : 'cursor-default'
                    }`}
                >
                    <div className="flex items-center justify-center gap-2 mb-1">
                        <CheckCircle className="w-5 h-5 text-green-500" />
                        <span className="text-sm text-gray-600">Correct</span>
                    </div>
                    <p className="text-3xl font-bold text-green-600">{stats.correct}</p>
                    {stats.correct > 0 && (
                        <div className="flex items-center justify-center gap-1 mt-2 text-xs text-green-600">
                            <span>Review</span>
                            <ChevronRight className="w-3 h-3" />
                        </div>
                    )}
                </button>

                {/* Incorrect - Clickable */}
                <button
                    onClick={() => stats.incorrect > 0 && onReviewClick(category, 'incorrect')}
                    disabled={stats.incorrect === 0}
                    className={`bg-white rounded-xl p-4 text-center transition-all ${
                        stats.incorrect > 0
                            ? 'hover:shadow-md hover:scale-[1.02] cursor-pointer'
                            : 'cursor-default'
                    }`}
                >
                    <div className="flex items-center justify-center gap-2 mb-1">
                        <XCircle className="w-5 h-5 text-red-500" />
                        <span className="text-sm text-gray-600">Incorrect</span>
                    </div>
                    <p className="text-3xl font-bold text-red-600">{stats.incorrect}</p>
                    {stats.incorrect > 0 && (
                        <div className="flex items-center justify-center gap-1 mt-2 text-xs text-red-600">
                            <span>Review</span>
                            <ChevronRight className="w-3 h-3" />
                        </div>
                    )}
                </button>
            </div>

            <div className="bg-white rounded-xl p-4">
                <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                        <Target className={`w-5 h-5 ${colors.accent}`} />
                        <span className="text-sm text-gray-600">Accuracy</span>
                    </div>
                    <span className={`text-2xl font-bold ${colors.accent}`}>
                        {stats.accuracy}%
                    </span>
                </div>

                {/* Progress bar */}
                <div className="w-full bg-gray-200 rounded-full h-3">
                    <div
                        className={`h-3 rounded-full transition-all duration-500 ${
                            color === 'purple' ? 'bg-purple-500' : 'bg-blue-500'
                        }`}
                        style={{ width: `${stats.accuracy}%` }}
                    />
                </div>

                <p className="text-xs text-gray-500 mt-2 text-center">
                    {stats.total_answered} questions answered
                </p>
            </div>
        </div>
    );
};

const ResultsPage = ({ onBack, onReviewQuestions }) => {
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const auth = getAuth();
    const user = auth.currentUser;

    useEffect(() => {
        const fetchStats = async () => {
            if (!user) {
                setLoading(false);
                return;
            }

            try {
                const token = await user.getIdToken(true);
                const response = await fetch(`${API_URL}/api/account/stats`, {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    }
                });

                if (!response.ok) {
                    throw new Error('Failed to fetch stats');
                }

                const data = await response.json();
                setStats(data);
            } catch (err) {
                console.error('Error fetching stats:', err);
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        fetchStats();
    }, [user]);

    const totalAnswered = stats
        ? stats.ostomy.total_answered + stats.wound_care.total_answered
        : 0;

    const totalCorrect = stats
        ? stats.ostomy.correct + stats.wound_care.correct
        : 0;

    const overallAccuracy = totalAnswered > 0
        ? Math.round((totalCorrect / totalAnswered) * 100)
        : 0;

    const handleReviewClick = (category, filter) => {
        if (onReviewQuestions) {
            onReviewQuestions(category, filter);
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-4">
            <div className="max-w-3xl mx-auto">
                {/* Header */}
                <div className="flex items-center gap-4 mb-6">
                    <button
                        onClick={onBack}
                        className="p-2 bg-white rounded-full shadow-md hover:shadow-lg transition-all"
                    >
                        <ArrowLeft className="w-6 h-6 text-gray-700" />
                    </button>
                    <div className="flex items-center gap-3">
                        <div className="p-3 bg-gradient-to-br from-slate-600 to-slate-800 rounded-xl">
                            <BarChart3 className="w-6 h-6 text-white" />
                        </div>
                        <h1 className="text-2xl font-bold text-gray-800">My Results</h1>
                    </div>
                </div>

                {loading ? (
                    <div className="flex flex-col items-center justify-center py-20">
                        <Loader2 className="w-12 h-12 animate-spin text-slate-500 mb-4" />
                        <p className="text-gray-600">Loading your results...</p>
                    </div>
                ) : error ? (
                    <div className="bg-red-50 border-2 border-red-200 rounded-2xl p-6 text-center">
                        <XCircle className="w-12 h-12 text-red-500 mx-auto mb-3" />
                        <p className="text-red-700">Failed to load results</p>
                        <p className="text-sm text-red-600 mt-1">{error}</p>
                    </div>
                ) : (
                    <>
                        {/* Overall Summary - Not clickable */}
                        <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
                            <h2 className="text-lg font-semibold text-gray-700 mb-4">Overall Progress</h2>
                            <div className="grid grid-cols-3 gap-4 text-center">
                                <div>
                                    <p className="text-3xl font-bold text-slate-700">{totalAnswered}</p>
                                    <p className="text-sm text-gray-500">Total Answered</p>
                                </div>
                                <div>
                                    <p className="text-3xl font-bold text-green-600">{totalCorrect}</p>
                                    <p className="text-sm text-gray-500">Correct</p>
                                </div>
                                <div>
                                    <p className="text-3xl font-bold text-slate-700">{overallAccuracy}%</p>
                                    <p className="text-sm text-gray-500">Accuracy</p>
                                </div>
                            </div>
                        </div>

                        {/* Category Stats - Clickable */}
                        <div className="space-y-4">
                            <StatCard
                                title="Ostomy"
                                stats={stats?.ostomy || { correct: 0, incorrect: 0, accuracy: 0, total_answered: 0 }}
                                color="purple"
                                category="ostomy"
                                onReviewClick={handleReviewClick}
                            />
                            <StatCard
                                title="Wound Care"
                                stats={stats?.wound_care || { correct: 0, incorrect: 0, accuracy: 0, total_answered: 0 }}
                                color="blue"
                                category="wound_care"
                                onReviewClick={handleReviewClick}
                            />
                        </div>

                        {/* Empty state */}
                        {totalAnswered === 0 && (
                            <div className="mt-6 bg-white rounded-2xl p-8 text-center">
                                <BarChart3 className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                                <p className="text-gray-600 mb-2">No results yet</p>
                                <p className="text-sm text-gray-500">
                                    Start studying or take a quiz to see your progress here!
                                </p>
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

export default ResultsPage;
